import { request, ROUTES } from "./api";
import { createClientRequestId } from "../utils/clientRequestId";

const BUSINESS_APPLICATION_UPLOAD_SESSION_KEY = "business_application_upload_session";

function getActiveBusinessId() {
  try {
    const raw = localStorage.getItem("business_session");
    const parsed = raw ? JSON.parse(raw) : null;
    const businessId = Number(parsed?.activeBusinessId || parsed?.businessId || 0);
    return Number.isInteger(businessId) && businessId > 0 ? businessId : null;
  } catch {
    return null;
  }
}

function buildErrorMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload?.error?.message === "string") return payload.error.message;
  if (typeof payload?.message === "string") return payload.message;
  return fallback;
}

function validateAgainstConstraints(file, constraints = {}) {
  const allowedMimeTypes = Array.isArray(constraints.allowedMimeTypes) ? constraints.allowedMimeTypes : [];
  const allowedFormats = Array.isArray(constraints.allowedFormats) ? constraints.allowedFormats : [];
  const maxBytes = Number(constraints.maxBytes || 0);
  const extension = String(file?.name || "").includes(".")
    ? String(file.name).split(".").pop().trim().toLowerCase()
    : "";
  const normalizedType = String(file?.type || "").trim().toLowerCase();
  const mimeRequired = constraints.mimeRequired === true;

  const mimeAllowed = normalizedType && allowedMimeTypes.includes(normalizedType);
  const extensionAllowed = extension && allowedFormats.includes(extension);

  if (allowedMimeTypes.length && !mimeAllowed && (mimeRequired || normalizedType !== "" || !extensionAllowed)) {
    throw new Error("Request failed: Unsupported file type for this upload.");
  }
  if (maxBytes > 0 && Number(file?.size || 0) > maxBytes) {
    throw new Error(`Request failed: File exceeds the maximum allowed size of ${Math.round(maxBytes / (1024 * 1024))} MB.`);
  }
}

async function uploadToCloudinary({ uploadUrl, fields, file }) {
  const formData = new FormData();
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value == null) return;
    formData.append(key, value);
  });
  formData.append("file", file);

  const response = await fetch(uploadUrl, {
    method: "POST",
    body: formData,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${buildErrorMessage(payload, "Cloudinary upload failed.")}`);
  }

  return payload;
}

function readPendingFinalize(key) {
  if (!key) return null;
  try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch { return null; }
}

function writePendingFinalize(key, value) {
  if (!key) return;
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch {
    // Session resume is best-effort; the server remains authoritative.
  }
}

async function performDirectUpload({
  context,
  file,
  payload = {},
  headers = {},
  resumeKey = null,
  onStage = null,
  requireMime = false,
}) {
  if (!file) throw new Error("Request failed: Missing file");

  let pending = readPendingFinalize(resumeKey);
  if (!pending?.intentToken || !pending?.uploadResult) {
    onStage?.("preparing");
    const initResponse = await request({
      url: ROUTES.assetUploadInit,
      method: "POST",
      data: {
        context,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        ...payload,
      },
      headers,
    });

    const initData = initResponse?.data || initResponse;
    validateAgainstConstraints(file, {
      ...(initData?.upload?.constraints || {}),
      ...(requireMime ? { mimeRequired: true } : {}),
    });
    onStage?.("uploading");
    const uploadResult = await uploadToCloudinary({
      uploadUrl: initData?.upload?.url,
      fields: initData?.upload?.fields,
      file,
    });
    pending = {
      intentToken: initData?.intentToken,
      uploadResult,
      guestUploadSessionToken: initData?.guestUploadSessionToken || null,
      finalizeIdempotencyKey: createClientRequestId("asset_finalize"),
    };
    writePendingFinalize(resumeKey, pending);
  }

  if (!pending.finalizeIdempotencyKey) {
    pending.finalizeIdempotencyKey = createClientRequestId("asset_finalize");
    writePendingFinalize(resumeKey, pending);
  }

  onStage?.("finalizing");
  const finalizeResponse = await request({
    url: ROUTES.assetUploadFinalize,
    method: "POST",
    data: {
      intentToken: pending.intentToken,
      uploadResult: pending.uploadResult,
    },
    headers: { ...headers, "Idempotency-Key": pending.finalizeIdempotencyKey },
  });

  const finalized = finalizeResponse?.data || finalizeResponse;
  writePendingFinalize(resumeKey, null);
  onStage?.("complete");
  return {
    ...finalized,
    ...(pending.guestUploadSessionToken
      ? { guestUploadSessionToken: pending.guestUploadSessionToken }
      : {}),
  };
}

export function getBusinessApplicationUploadSessionToken() {
  try {
    return sessionStorage.getItem(BUSINESS_APPLICATION_UPLOAD_SESSION_KEY) || null;
  } catch {
    return null;
  }
}

export function setBusinessApplicationUploadSessionToken(token) {
  if (!token) return;
  try {
    sessionStorage.setItem(BUSINESS_APPLICATION_UPLOAD_SESSION_KEY, token);
  } catch {
    // no-op
  }
}

export function clearBusinessApplicationUploadSessionToken() {
  try {
    sessionStorage.removeItem(BUSINESS_APPLICATION_UPLOAD_SESSION_KEY);
  } catch {
    // no-op
  }
}

export async function uploadAvatarAsset(file, options = {}) {
  const result = await performDirectUpload({
    context: "user.avatar",
    file,
    headers: options.headers || {},
  });

  return {
    success: true,
    data: {
      avatarUrl: result?.avatarUrl || result?.secureUrl || "",
    },
  };
}

export async function uploadBusinessProductImageAsset(file, meta = {}, options = {}) {
  const businessId = Number(meta?.businessId || getActiveBusinessId() || 0);
  if (!Number.isInteger(businessId) || businessId <= 0) {
    throw new Error("Request failed: Missing active business context for product upload.");
  }

  const result = await performDirectUpload({
    context: "business.product.image",
    file,
    payload: {
      businessId,
      productId: meta?.productId || null,
      type: meta?.type || "product",
      variantKey: meta?.variantKey || null,
    },
    headers: { "X-Portal": "business", ...(options.headers || {}) },
  });

  const url = result?.url || result?.imageUrl || result?.secureUrl || "";

  return {
    success: true,
    data: {
      ...result,
      url,
      imageUrl: url,
    },
    url,
    imageUrl: url,
  };
}

async function uploadBusinessProductVariant3DAssetByContext(context, file, meta = {}, options = {}) {
  const businessId = Number(meta?.businessId || getActiveBusinessId() || 0);
  if (!Number.isInteger(businessId) || businessId <= 0) {
    throw new Error("Request failed: Missing active business context for 3D upload.");
  }

  const result = await performDirectUpload({
    context,
    file,
    payload: {
      businessId,
      productId: meta?.productId || null,
      variantId: meta?.variantId || null,
      variantKey: meta?.variantKey || null,
    },
    headers: { "X-Portal": "business", ...(options.headers || {}) },
  });

  return {
    success: true,
    data: result,
    assetId: result?.assetId || null,
    url: result?.url || result?.secureUrl || null,
    resourceType: result?.resourceType || null,
    fileName: result?.fileName || null,
    fileSize: result?.fileSize ?? null,
  };
}

export async function uploadBusinessProduct3DModelAsset(file, meta = {}, options = {}) {
  return uploadBusinessProductVariant3DAssetByContext(
    "business.product.3d_model",
    file,
    meta,
    options
  );
}

export async function uploadBusinessProduct3DPosterAsset(file, meta = {}, options = {}) {
  return uploadBusinessProductVariant3DAssetByContext(
    "business.product.3d_poster",
    file,
    meta,
    options
  );
}

export async function uploadBusinessApplicationDocumentAsset(file, meta = {}, options = {}) {
  const fieldName = String(meta?.fieldName || meta?.documentType || "").trim().toLowerCase();
  const context = fieldName === "permit" || fieldName === "dti_sec_permit"
    ? "business.application.permit"
    : "business.application.additional_document";

  const guestUploadSessionToken = meta?.guestUploadSessionToken || getBusinessApplicationUploadSessionToken();
  const result = await performDirectUpload({
    context,
    file,
    payload: {
      applicationId: meta?.applicationId || null,
      guestUploadSessionToken: guestUploadSessionToken || null,
    },
    headers: options.headers || {},
  });

  if (result?.guestUploadSessionToken) {
    setBusinessApplicationUploadSessionToken(result.guestUploadSessionToken);
  }

  return {
    success: true,
    data: result,
    assetId: result?.assetId || null,
    guestUploadSessionToken: result?.guestUploadSessionToken || guestUploadSessionToken || null,
  };
}

export async function uploadMessagingAttachmentAsset(file, meta = {}, options = {}) {
  const result = await performDirectUpload({
    context: "messaging.attachment",
    file,
    payload: {
      conversationId: meta?.conversationId || null,
    },
    headers: options.headers || {},
  });

  return {
    success: true,
    data: result,
    assetId: result?.assetId || null,
    fileName: result?.fileName || null,
    fileType: result?.fileType || null,
    fileSize: result?.fileSize ?? null,
    resourceType: result?.resourceType || null,
  };
}

export async function uploadDeliveryProofPhoto(file, meta = {}, options = {}) {
  const result = await performDirectUpload({
    context: "delivery.proof.photo",
    file,
    payload: meta,
    headers: { "X-Portal": "business", ...(options.headers || {}) },
    resumeKey: options.resumeKey || null,
    onStage: options.onStage || null,
    requireMime: true,
  });
  return {
    success: true,
    data: result,
    assetId: result?.assetId || null,
    proof: result?.proof || null,
  };
}

export async function uploadRecruitmentResume(file, jobId) {
  return performDirectUpload({ context: 'recruitment.resume', file,
    payload: { jobId },
    headers: { 'X-Portal': 'guest' } });
}

export async function uploadRecruitmentAssessmentForm(file, businessId, applicationId) {
  return performDirectUpload({ context: 'recruitment.assessment_form', file,
    payload: { businessId, applicationId },
    headers: { 'X-Portal': 'business' } });
}

export async function uploadCandidateRequirement(file, candidateActionToken, requirementId) {
  return performDirectUpload({ context: 'recruitment.requirement', file,
    payload: { candidateActionToken, requirementId },
    headers: { 'X-Portal': 'guest' } });
}
